// https://segment.com/docs/spec/ecommerce/v2/#product-list-viewed

import getWebShellProductOptions from '@nike/shop-apis/web-shell-client/product/get-options';
import queryClient from '@nike/shop-apis/utils/query-client';

/**
 * Shapes a single product from the WebShell `product` capability cache into the
 * Segment Product List Viewed payload. The output field names are dictated by the
 * Segment ecommerce schema (and downstream dashboards key on `styleColor`,
 * `cloudProductId`, etc.) — those are preserved. Only the cache lookup itself
 * moves over to productCode.
 *
 * `productId` is taken in alongside `productCode` so `cloudProductId` keeps a
 * value even when the WebShell `product` cache hasn't resolved yet (e.g. cart
 * viewed on first paint). Pre-migration, `cloudProductId` was always populated
 * from the cart-item UUID; preserve that contract for downstream dashboards.
 *
 * @param {object} args
 * @param {string} [args.productId] - The cart/wishlist line-item UUID. Used as the
 *   `cloudProductId` fallback when the WebShell cache hasn't resolved.
 * @param {string} args.productCode - The hydration primary key (formerly styleColor).
 * @returns {object} Segment-shaped product payload.
 */
export const shapeProductListData = ({ productId, productCode } = {}) => {
  const { queryKey } = getWebShellProductOptions({ productCode });
  const product = queryClient.getQueryData(queryKey) || {};

  const { internalPid, productType, brand, productCode: cachedProductCode, title } = product;

  return {
    pid: internalPid,
    productId: internalPid,
    // Fall back to the line-item UUID so cold-cache analytics still ships an id
    // (matches the pre-migration behavior where `cloudProductId` was always set
    // from the cart-internal `productId`).
    cloudProductId: product?.merchProductId || productId,
    prodigyProductId: internalPid,
    productType,
    brand,
    productName: title,
    styleColor: cachedProductCode,
  };
};
